'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { CrossDark } from 'assets/icons';
import CustomLink from 'components/shared/CustomLink';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { classifyRoute } from 'lib/consent/policy';
import styles from 'components/shared/CookieConsentBanner.module.css';
import { useConsentRuntime } from './ConsentRuntime';

export function NextStackBanner({ className }: { className?: string }) {
  const t = useTranslations('common');
  const pathname = useInternalPathname();
  const {
    stack,
    choice,
    consentKnown,
    setBannerReady,
    choose,
    openPrivacyChoices,
  } = useConsentRuntime();
  const [closed, setClosed] = useState(false);
  const page = classifyRoute(pathname);
  const show =
    stack === 'next' &&
    consentKnown &&
    choice === 'none' &&
    page !== 'private' &&
    !closed;

  useEffect(() => {
    if (show) setBannerReady(true);
  }, [show, setBannerReady]);

  if (!show) return null;

  return (
    <div
      className={className ? `${styles.root} ${className}` : styles.root}
      role="region"
      aria-label={t('cookieBanner.heading')}
    >
      <div className={styles.content}>
        <h2 className={styles.heading}>
          {t('cookieBanner.heading')}
          <button
            className={styles.closeButton}
            type="button"
            aria-label={t('cookieBanner.close')}
            onClick={() => setClosed(true)}
          >
            <CrossDark fill="rgba(255, 255, 255, 0.5)" />
          </button>
        </h2>
        <p className={styles.description}>
          {t('cookieBanner.description')}
          <CustomLink
            className={styles.policyLink}
            href="https://ultrahumanapp.notion.site/Cookie-Policy-ebcff6b1c95744fb9471ee874fc544e7"
            target="_blank"
            rel="noreferrer"
          >
            {t('cookieBanner.policyLink')}
          </CustomLink>
        </p>
        <span className={styles.actions}>
          <button
            className={styles.actionButton}
            type="button"
            onClick={() => choose('reject')}
          >
            {t('cookieBanner.REJECT')}
          </button>
          <button
            className={`${styles.actionButton} ${styles.primary}`}
            type="button"
            onClick={() => choose('accept')}
          >
            {t('cookieBanner.ACCEPT')}
          </button>
          <button
            className={styles.actionButton}
            type="button"
            onClick={openPrivacyChoices}
          >
            {t('cookieBanner.manage')}
          </button>
        </span>
      </div>
    </div>
  );
}
